import { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import useTourV2PurchasableOptionDetails from 'hooks/TourV2/useTourV2PurchasableOptionDetails'
import { TourV2ContentSections } from 'constants/tours'
import PriceRowPriceWithCaption from 'LuxKit/PricePoints/PriceRowPriceWithCaption'
import PriceRowCaption from 'LuxKit/PricePoints/PriceRowCaption'
import OfferPageStickyFooter from 'components/OfferPage/OfferPageStickyFooter'
import ModalContext from 'contexts/ModalContext'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import TourV2DepartureBookingModal from '../Departures/TourV2DepartureBookingModal'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import PriceRowLuxPlusPriceWithCaption from 'LuxKit/PricePoints/LuxPlus/PriceRowLuxPlusPriceWithCaption'
import { getTourPricePerDay, getTourV2VariationDuration } from 'lib/tours/tourUtils'
import { useAppSelector } from 'hooks/reduxHooks'
import { getTourPriceMode } from 'selectors/TourV2/toursSelectors'
import styled from 'styled-components'

const PriceDetails = styled.div`
  min-width: 0;
`

const WrappingMemberPrice = styled(PriceRowLuxPlusPriceWithCaption)`
  /* LuxPlus price rows do not expose wrap. Their default wrap="no-wrap" makes
     Typography emit wrap-no-wrap; revisit this override if LuxKit changes that
     implementation. Repeat the consumer selector to outrank Typography's nowrap rule
     regardless of stylesheet injection order. */
  &&.wrap-no-wrap {
    flex-wrap: wrap;
    white-space: normal;
  }
`

interface Props {
  purchasableOption?: App.Tours.TourV2OfferPurchasableOption
  onScrollTo: (section: TourV2ContentSections) => void
  visible: boolean
  offer: App.Tours.TourV2Offer
  variation: App.Tours.TourV2OfferVariation
}

function TourV2StickyFooter(props: Props) {
  const { purchasableOption, onScrollTo, visible, offer, variation } = props

  const intl = useIntl()
  const purchasableOptionDetails = useTourV2PurchasableOptionDetails(purchasableOption)
  const tourPriceMode = useAppSelector(getTourPriceMode)

  const duration = useMemo(() => {
    return getTourV2VariationDuration(variation)
  }, [variation])

  const pricePerDayDetails = useMemo(() => {
    if (!purchasableOptionDetails || tourPriceMode === 'total') return
    return getTourPricePerDay(purchasableOptionDetails.pricePerPerson, purchasableOptionDetails.memberPricePerPerson, duration)
  }, [purchasableOptionDetails, duration, tourPriceMode])

  const isMobile = useScreenSizeOnly('mobile')
  const showModal = useContext(ModalContext)
  const onCheckAvailability = useCallback(() => {
    if (isMobile) {
      showModal(<TourV2DepartureBookingModal offer={offer} />)
    } else {
      onScrollTo(TourV2ContentSections.DEPARTURES)
    }
  }, [isMobile, offer, onScrollTo, showModal])

  return (
    <OfferPageStickyFooter
      visible={visible}
      callToActionCopy={intl.formatMessage({
        id: 'fFgh7zR4gKzveK0B5otV',
        defaultMessage: 'Check Availability',
        description: 'Call-to-action button text to check availability for the current offer or property',
      })}
      onCTAClick={onCheckAvailability}
    >
      {!!purchasableOptionDetails && <PriceDetails>
        {!pricePerDayDetails && <>
          {!purchasableOptionDetails.memberPricePerPerson && <PriceRowPriceWithCaption
            size="M"
            wrap="word-wrap"
            caption={intl.formatMessage({
              id: 'eYSFqXVFsgoMsnbiqQuW',
              defaultMessage: 'From',
              description: 'Price caption prefix text indicating the starting price for an offer or package',
            })}
            price={purchasableOptionDetails.pricePerPerson}
            saleUnit={intl.formatMessage({
              id: '1YGBd2FNRf8Gh-jkTg38',
              defaultMessage: 'person',
              description: 'Sale unit for per person pricing in the tours section',
            })}
          />}
          {!!purchasableOptionDetails.memberPricePerPerson && <WrappingMemberPrice
            size="M"
            caption={intl.formatMessage({
              id: 'eYSFqXVFsgoMsnbiqQuW',
              defaultMessage: 'From',
              description: 'Price caption prefix text indicating the starting price for an offer or package',
            })}
            memberPrice={purchasableOptionDetails.memberPricePerPerson}
            saleUnit={intl.formatMessage({
              id: '1YGBd2FNRf8Gh-jkTg38',
              defaultMessage: 'person',
              description: 'Sale unit for per person pricing in the tours section',
            })}
          />}
        </>}
        {
          pricePerDayDetails &&
          <>
            {!pricePerDayDetails?.memberPricePerPersonPerDay && <PriceRowPriceWithCaption
              size="M"
              wrap="word-wrap"
              caption={intl.formatMessage({
                id: 'eYSFqXVFsgoMsnbiqQuW',
                defaultMessage: 'From',
                description: 'Price caption prefix text indicating the starting price for an offer or package',
              })}
              price={pricePerDayDetails.pricePerPersonPerDay}
              saleUnit={intl.formatMessage({
                id: 'e8hsjPAyKDx8tVuwKXFu',
                defaultMessage: 'day per person',
                description: 'Sale unit for per day per person pricing in the tours section',
              })}
            />}
            {!!pricePerDayDetails.memberPricePerPersonPerDay && <WrappingMemberPrice
              size="M"
              caption={intl.formatMessage({
                id: 'eYSFqXVFsgoMsnbiqQuW',
                defaultMessage: 'From',
                description: 'Price caption prefix text indicating the starting price for an offer or package',
              })}
              memberPrice={pricePerDayDetails.memberPricePerPersonPerDay}
              saleUnit={intl.formatMessage({
                id: 'e8hsjPAyKDx8tVuwKXFu',
                defaultMessage: 'day per person',
                description: 'Sale unit for per day per person pricing in the tours section',
              })}
            />}
          </>
        }

        <PriceRowCaption>
          {purchasableOptionDetails.roomTypeName}
        </PriceRowCaption>
      </PriceDetails>}
    </OfferPageStickyFooter>
  )
}

type MappedProps = Pick<Props, 'offer'>

export default withOfferPageState((state): MappedProps => ({
  offer: state.tourV2Offer!,
}), TourV2StickyFooter)
